import { useRouter } from 'next/router';
import { useState, useRef } from 'react';
import { Breadcrumb as AriaBreadcrumb, Breadcrumbs as AriaBreadcrumbs, Link as AriaLink, Pressable } from 'react-aria-components';

import { useI18n } from 'context/i18n';
import { baseTranslations } from 'utils/translations';
import { useLocale } from 'hooks/useLocale';
import { getDomainValue, isDomain } from 'utils/domain';
import { BreadcrumbsLdJson } from 'components/ld-json/breadcrubms';
import type { BreadcrumbItem } from 'components/ui/breadcrumbs';

const fragmentsToOmit = ['landings', 'accessoris'];

function BreadcrumbItem({ children }: { children: React.ReactNode }) {
  return (
    <AriaBreadcrumb className="inline-flex justify-between bg-[#dfdfdf] px-4 py-1.5 text-copy/45 last-of-type:text-copy hover:text-copy last-of-type:nnormal:bg-neutral-inverted last-of-type:nnormal:text-inverted [&_a]:text-inherit [&_a]:transition-colors">
      {children}
    </AriaBreadcrumb>
  );
}

type BreadcrumbProps = {
  fragments: TODO[];
  i18nKeys?: TODO;
  onClick?: (name: string) => void;
  changeLast?: () => string;
  camperlab?: boolean;
  pageName?: string | null;
  withLdJson?: boolean;
};

export function Breadcrumb({ fragments = [], i18nKeys = {}, onClick, changeLast, camperlab = false, pageName = null, withLdJson = true }: BreadcrumbProps) {
  const { t } = useI18n();
  const { country, locale } = useLocale();
  const [breadcrumbs, setBreadcrumbs] = useState<(BreadcrumbItem & { href?: string; onClick?: (() => void) | undefined })[]>([]);
  const hasFragments = useRef(false);
  const router = useRouter();

  if (!hasFragments.current) {
    const newBreadcrumbs: typeof breadcrumbs = [];

    if (Array.isArray(fragments) && fragments.length > 0) {
      fragments.forEach((item, index) => {
        newBreadcrumbs.push({
          href: item.href,
          name: item.title,
          id: `bc-${index}`,
        });
      });
    } else {
      const { asPath, query } = router;
      const pathSnippets = asPath.split('/').filter((filterItem) => !fragmentsToOmit.includes(filterItem));
      // Elimina el idioma
      pathSnippets.shift();
      newBreadcrumbs.push({
        href: `/${locale}`,
        name: getDomainValue({
          camper: 'Camper',
          nnormal: 'Nnormal',
          camperlab: 'Camperlab',
        }),
        id: 'bc-0',
      });
      const pathNames = pathSnippets.slice();
      const queryKeys = new Set(Object.keys(query));
      let includesTarget = false;
      let includesProduct = false;
      if (!(queryKeys.has('target') && queryKeys.has('product'))) {
        Object.keys(baseTranslations.target).forEach(
          (key) => (includesTarget = includesTarget || baseTranslations.target[key as keyof typeof baseTranslations.target].includes(pathSnippets[0] ?? '')),
        );
        Object.keys(baseTranslations.product).forEach(
          (key) =>
            (includesProduct = includesProduct || baseTranslations.product[key as keyof typeof baseTranslations.product].includes(pathSnippets[1] ?? '')),
        );
      }
      // Juntar target y product
      if ((queryKeys.has('target') && queryKeys.has('product')) || (includesTarget && includesProduct)) {
        pathSnippets[1] = `${pathSnippets[0]}/${pathSnippets[1]}`;
        pathSnippets.shift();
        if (queryKeys.has('brand') && !i18nKeys.brand) {
          // basicamente para cuando te llega en una url tipo:
          // es_ES/hombre/zapatos/jfdwpjifepiwnfiewpif no te meta ese chorro inventado en el breadcrumbs e intente "traducirlo"
          pathSnippets.pop();
        }
        if (locale === 'th_TH') {
          pathNames[1] = `${i18nKeys.family ? t('menu.lateral', `familia.${i18nKeys.family}`) : pathNames[1]} ${i18nKeys.target ? t('menu.lateral', i18nKeys.target) : pathNames[0]}`;
        } else if (country === 'JP' && i18nKeys.target === '2') {
          pathNames[1] = `${i18nKeys.target ? t('menu.lateral', i18nKeys.target) : pathNames[0]}`;
        } else {
          pathNames[1] = `${i18nKeys.target ? t('menu.lateral', i18nKeys.target) : pathNames[0]} ${i18nKeys.family ? t('menu.lateral', `familia.${i18nKeys.family}`) : pathNames[1]}`;
        }
        pathNames.shift();
      }

      const lastName = pathSnippets.length - 1;

      if (pathNames[lastName]?.includes('?')) {
        pathNames[lastName] = pathNames[lastName].split('?')[0] ?? '';
      }

      if (pathNames[lastName]?.includes('#')) {
        pathNames[lastName] = pathNames[lastName].split('#')[0] ?? '';
      }

      if (queryKeys.has('brand') && i18nKeys.brand) {
        pathNames[1] = i18nKeys.brand;
      }

      pathSnippets.forEach((item, index) => {
        if (isDomain('nnormal') && item === router.query.brand) {
          return null;
        }

        const name = decodeURIComponent(pathNames[index] ?? '').replace(/[_-]/g, ' ');

        const url = `/${pathSnippets.slice(0, index + 1).join('/')}`;
        newBreadcrumbs.push({
          name: name,
          href: `/${locale}${url}`,
          onClick: onClick ? () => onClick?.(name) : undefined,
          id: `bec-${index}`,
        });
      });

      if (changeLast && typeof changeLast === 'function') {
        const lastUrl = `/${pathSnippets.slice(0, newBreadcrumbs.length).join('/')}`;

        newBreadcrumbs[newBreadcrumbs.length - 1] = {
          name: changeLast(),
          href: lastUrl,
          onClick: () => onClick?.(changeLast()),
          id: `bc-${newBreadcrumbs.length - 1}`,
        };
      }

      // CASO ESPECIAL PARA URL IPAD QUE TIENE SKU EN ?OPTION
      if (pageName === 'PDP' && pathSnippets[0]?.startsWith('producto')) {
        const { option } = query;

        newBreadcrumbs.push({ name: typeof option === 'string' ? option : (option?.join(' ') ?? ''), id: 'sku-breadcrumb' });
      }
    }
    setBreadcrumbs(newBreadcrumbs);
    hasFragments.current = true;
  }

  return (
    <>
      {withLdJson ?
        <BreadcrumbsLdJson breadcrumbs={breadcrumbs} withCurrent />
      : null}
      <AriaBreadcrumbs className="wrap flex w-full gap-x-[5px] gap-y-[15px] px-2.5 py-4 text-center text-xxs uppercase">
        {breadcrumbs.length === 0 && (
          <BreadcrumbItem>
            <AriaLink href={`/${locale}${!camperlab ? '/' : '/content/camperlab'}`}>
              {!camperlab ? t('generico', 'breadcrumb.home', '**Home') : t('generico', 'breadcrumb.camperlab', '**Camperlab')}
            </AriaLink>
          </BreadcrumbItem>
        )}
        {breadcrumbs.map((f) => (
          <BreadcrumbItem key={f.id}>
            {f.href ?
              <AriaLink href={f.href} onClick={f.onClick ?? (() => {})}>
                {f.name}
              </AriaLink>
            : f.onClick ?
              <Pressable onPress={f.onClick}>
                <span role="button">{f.name}</span>
              </Pressable>
            : <span>{f.name}</span>}
          </BreadcrumbItem>
        ))}
      </AriaBreadcrumbs>
    </>
  );
}
